import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
  TrendingUp, Package, Truck, AlertTriangle, Clock, Filter, RotateCw, LogIn,
} from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString("id-ID");

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function KPI({ icon: Icon, label, value, hint, accent = "text-blue-700", testid }) {
  return (
    <Card className="rounded-sm border-slate-200 shadow-sm p-4 bg-white" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-ind">{label}</div>
          <div className="mt-2 text-3xl font-bold tracking-tight text-slate-900">{value}</div>
          {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
        </div>
        <div className={`w-9 h-9 rounded-sm bg-slate-50 flex items-center justify-center ${accent}`}>
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [filter, setFilter] = useState({ tanggal_dari: "", tanggal_sampai: "" });
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  const loadLatest = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/latest");
      const t = data.tanggal || todayIso();
      setFilter({ tanggal_dari: t, tanggal_sampai: t });
    } catch {
      const t = todayIso();
      setFilter({ tanggal_dari: t, tanggal_sampai: t });
    } finally {
      setInitialized(true);
    }
  }, []);

  const loadView = useCallback(async (f) => {
    setLoading(true);
    try {
      const params = {};
      if (f.tanggal_dari) params.tanggal_dari = f.tanggal_dari;
      if (f.tanggal_sampai) params.tanggal_sampai = f.tanggal_sampai;
      const { data } = await api.get("/dashboard/view", { params });
      setView(data);
    } catch {
      toast.error("Gagal memuat dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLatest(); }, [loadLatest]);
  useEffect(() => {
    if (!initialized) return;
    loadView(filter);
  }, [filter, initialized, loadView]);

  const resetLatest = async () => {
    await loadLatest();
  };

  if (!initialized || (loading && !view)) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-56" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-72" />
      </div>
    );
  }

  const kpis = view?.kpis || {};
  const kumC = view?.kumulatif?.casting || [];
  const kumP = view?.kumulatif?.pengiriman || [];
  const problems = view?.problems || [];
  const laporan = view?.laporan || [];
  const pengirimanMaterial = view?.pengiriman_material || [];
  const stockAAList = Array.isArray(view?.stock_aa)
    ? view.stock_aa
    : (view?.stock_aa ? [view.stock_aa] : []);
  const infos = view?.infos || [];
  const meetings = view?.meetings || [];

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6 gap-2">
        <div>
          <div className="label-ind">Control Room</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Dashboard Produksi</h1>
          <p className="text-sm text-slate-500 mt-1">Ringkasan performa rodding plant berdasarkan rentang tanggal.</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <Clock className="w-4 h-4" />
          <span className="font-mono">{new Date().toLocaleString("id-ID")}</span>
          {!user && (
            <Button size="sm" variant="outline" onClick={() => nav("/login")} data-testid="btn-login-cta" className="rounded-sm">
              <LogIn className="w-3.5 h-3.5 mr-1" /> Masuk
            </Button>
          )}
        </div>
      </div>

      {/* Filter */}
      <Card className="rounded-sm border-slate-200 bg-white p-4 mb-6" data-testid="dashboard-filter">
        <div className="flex items-center gap-3 mb-3">
          <Filter className="w-4 h-4 text-slate-500" />
          <div className="label-ind">Filter Dashboard</div>
          <span className="text-xs font-mono text-slate-400">
            {view?.filter?.tanggal_dari} — {view?.filter?.tanggal_sampai}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="label-ind">Dari Tanggal</label>
            <Input type="date" value={filter.tanggal_dari} onChange={(e) => setFilter((f) => ({ ...f, tanggal_dari: e.target.value }))} data-testid="filter-tanggal-dari" className="h-9 mt-1 rounded-sm" />
          </div>
          <div>
            <label className="label-ind">Sampai Tanggal</label>
            <Input type="date" value={filter.tanggal_sampai} onChange={(e) => setFilter((f) => ({ ...f, tanggal_sampai: e.target.value }))} data-testid="filter-tanggal-sampai" className="h-9 mt-1 rounded-sm" />
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={resetLatest} data-testid="btn-filter-latest" className="h-9 rounded-sm w-full">
              <RotateCw className="w-4 h-4 mr-1" /> Data Terakhir
            </Button>
          </div>
          {loading && <div className="flex items-end text-xs text-slate-500 font-mono">Memuat...</div>}
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <KPI testid="kpi-total-reports" icon={TrendingUp} label="Total Laporan" value={fmt(kpis.total_reports)} hint="seluruh data" />
        <KPI testid="kpi-total-casting" icon={Package} label="Total Casting" value={fmt(kpis.total_casting_cumulative)} hint={`s/d ${view?.filter?.tanggal_sampai || "-"} · sch ${fmt(kpis.total_schedule_cumulative)}`} accent="text-orange-600" />
        <KPI testid="kpi-total-pengiriman" icon={Truck} label="Pengiriman AA" value={fmt(kpis.total_pengiriman_cumulative)} hint={`s/d ${view?.filter?.tanggal_sampai || "-"} · sch ${fmt(kpis.total_schedule_cumulative)}`} accent="text-slate-700" />
        <KPI testid="kpi-problems" icon={AlertTriangle} label="Problem" value={fmt(kpis.problems_count)} hint="dalam rentang" accent="text-red-600" />
      </div>

      {/* Kumulatif Produksi - 2 column per-shift */}
      <Card className="rounded-sm p-4 bg-white border-slate-200 mb-6" data-testid="widget-kumulatif">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="label-ind">Kumulatif Produksi</div>
            <h3 className="text-base font-semibold text-slate-900">Performa vs Schedule (per shift dalam rentang)</h3>
          </div>
          <div className="text-xs text-slate-500 font-mono">
            {kumC.length} entri
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <KumulatifColumn title="Casting" rows={kumC} accent="text-blue-700" bg="bg-blue-600" testid="kumulatif-casting" />
          <KumulatifColumn title="Pengiriman AA" rows={kumP} accent="text-orange-600" bg="bg-orange-500" testid="kumulatif-pengiriman" />
        </div>
      </Card>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="rounded-sm p-4 lg:col-span-2 bg-white border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-ind">Tren Produksi (14 hari terakhir)</div>
              <h3 className="text-base font-semibold text-slate-900">Casting vs Pengiriman AA</h3>
            </div>
          </div>
          <div className="h-72" data-testid="chart-trend">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={view?.trend || []} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="tanggal" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2, border: "1px solid #e2e8f0" }} labelStyle={{ fontWeight: 600 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="casting" stroke="#0f4c81" strokeWidth={2} name="Casting" dot={{ r: 3 }} />
                <Line type="monotone" dataKey="pengiriman" stroke="#ff5722" strokeWidth={2} name="Pengiriman AA" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="rounded-sm p-4 bg-white border-slate-200">
          <div className="mb-3">
            <div className="label-ind">Per Shift</div>
            <h3 className="text-base font-semibold text-slate-900">Breakdown Shift</h3>
          </div>
          <div className="h-64" data-testid="chart-shift">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={view?.by_shift || []} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="shift" tickFormatter={(v) => `Shift ${v}`} stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="casting" fill="#0f4c81" name="Casting" />
                <Bar dataKey="pengiriman" fill="#ff5722" name="Pengiriman" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Per Team + Problem */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="rounded-sm p-4 bg-white border-slate-200">
          <div className="mb-3">
            <div className="label-ind">Per Team Operasi</div>
            <h3 className="text-base font-semibold text-slate-900">Breakdown Team</h3>
          </div>
          <div className="h-64" data-testid="chart-team">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={view?.by_team || []} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#64748b" fontSize={11} />
                <YAxis dataKey="team" type="category" tickFormatter={(v) => `Team ${v}`} stroke="#64748b" fontSize={11} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="casting" fill="#0f4c81" name="Casting" />
                <Bar dataKey="pengiriman" fill="#ff5722" name="Pengiriman" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <ProblemWidget problems={problems} />
      </div>

      {/* Laporan compact */}
      <Card className="rounded-sm p-4 bg-white border-slate-200 mb-6" data-testid="widget-laporan">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="label-ind">Laporan</div>
            <h3 className="text-base font-semibold text-slate-900">Ringkasan Laporan Shift</h3>
          </div>
          <span className="text-xs text-slate-500 font-mono">{laporan.length} laporan</span>
        </div>
        <div className="overflow-x-auto border border-slate-200 rounded-sm">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th className="text-left">Tanggal</th>
                <th className="text-left">Shift</th>
                <th className="text-right">Kadar GC401 (%)</th>
                <th className="text-right">CE (%)</th>
                <th className="text-left">ATC Aktif</th>
                <th className="text-right">Available ATC</th>
                <th className="text-right">HF-401 (kg)</th>
                <th className="text-right">AC-401 (%)</th>
                <th className="text-right">Masuk Metal SCA (Kg)</th>
                <th className="text-right">Sisa Butt (Pcs)</th>
              </tr>
            </thead>
            <tbody>
              {laporan.length === 0 && (
                <tr><td colSpan={10} className="text-center py-6 text-slate-400">Tidak ada laporan di rentang tanggal ini.</td></tr>
              )}
              {laporan.map((r, i) => (
                <tr key={r.id_laporan || i} data-testid={`laporan-row-${i}`}>
                  <td className="font-mono">{r.tanggal}</td>
                  <td><span className="inline-block px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-semibold rounded-sm">Shift {r.shift}</span></td>
                  <td className="text-right font-mono">{fmt(r.kadar_gc401)}</td>
                  <td className="text-right font-mono">{fmt(r.ce)}</td>
                  <td className="font-mono text-xs">{r.atc_aktif || "-"}</td>
                  <td className="text-right font-mono">{fmt(r.available_atc)}</td>
                  <td className="text-right font-mono">{fmt(r.hf401)}</td>
                  <td className="text-right font-mono">{fmt(r.ac401)}</td>
                  <td className="text-right font-mono">{fmt(r.masuk_metal_sca)}</td>
                  <td className="text-right font-mono">{fmt(r.sisa_butt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pengiriman Material (Shift 2) */}
      <Card className="rounded-sm p-4 bg-white border-slate-200 mb-6" data-testid="widget-pengiriman-material">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="label-ind">Pengiriman Material</div>
            <h3 className="text-base font-semibold text-slate-900">Data Shift 2 Sesuai Rentang Tanggal</h3>
          </div>
          <span className="text-xs text-slate-500 font-mono">{pengirimanMaterial.length} data</span>
        </div>
        <div className="overflow-x-auto border border-slate-200 rounded-sm">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th className="text-left">Tanggal</th>
                <th className="text-right">Crust Over 30 mm</th>
                <th className="text-right">Aluminium ball</th>
                <th className="text-right">Metal Scrap</th>
                <th className="text-right">Dust Hammer mill</th>
                <th className="text-right">Aluminium Scrap</th>
                <th className="text-left">Material Masuk (Custom)</th>
              </tr>
            </thead>
            <tbody>
              {pengirimanMaterial.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-6 text-slate-400">Tidak ada data Pengiriman Material shift 2 di rentang ini.</td>
                </tr>
              )}
              {pengirimanMaterial.map((row, i) => (
                <tr key={row.id_laporan || `${row.tanggal}-${i}`} data-testid={`pengiriman-material-row-${i}`}>
                  <td className="font-mono">{row.tanggal || "-"}</td>
                  <td className="text-right font-mono">{fmt(row.crust_over_30)}</td>
                  <td className="text-right font-mono">{fmt(row.aluminium_ball)}</td>
                  <td className="text-right font-mono">{fmt(row.metal_scrap)}</td>
                  <td className="text-right font-mono">{fmt(row.dust_hammer_mill)}</td>
                  <td className="text-right font-mono">{fmt(row.aluminium_scrap)}</td>
                  <td className="text-xs text-slate-700">
                    {Array.isArray(row.material_masuk) && row.material_masuk.length > 0
                      ? row.material_masuk.map((it) => `${it?.name || "-"}: ${fmt(it?.kg)} kg`).join(", ")
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Stock AA */}
      <Card className="rounded-sm p-4 bg-white border-slate-200 mb-6" data-testid="widget-stock-aa">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="label-ind">Stock AA</div>
            <h3 className="text-base font-semibold text-slate-900">Data Stock Sesuai Rentang Tanggal Dashboard</h3>
          </div>
          <span className="text-xs text-slate-500 font-mono">{stockAAList.length} data</span>
        </div>
        <div className="overflow-x-auto border border-slate-200 rounded-sm">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th className="text-left">Tanggal</th>
                <th className="text-right">SP</th>
                <th className="text-right">NSP</th>
                <th className="text-right">Emergency Stock</th>
                <th className="text-right">Tebal</th>
                <th className="text-right">Tebal Cleaning</th>
                <th className="text-right">RB</th>
                <th className="text-right">ARB</th>
                <th className="text-left">Others</th>
              </tr>
            </thead>
            <tbody>
              {stockAAList.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-6 text-slate-400">
                    Belum ada data Stock AA pada rentang tanggal ini.
                  </td>
                </tr>
              )}
              {stockAAList.map((stockAA, idx) => (
                <tr key={`${stockAA?.tanggal || "tgl"}-${idx}`} data-testid={`stock-aa-row-${idx}`}>
                  <td className="font-mono">{stockAA?.tanggal || "-"}</td>
                  <td className="text-right font-mono">{fmt(stockAA?.sp)}</td>
                  <td className="text-right font-mono">{fmt(stockAA?.nsp)}</td>
                  <td className="text-right font-mono">{fmt(stockAA?.emergency_stock)}</td>
                  <td className="text-right font-mono">{fmt(stockAA?.tebal)}</td>
                  <td className="text-right font-mono">{fmt(stockAA?.tebal_cleaning)}</td>
                  <td className="text-right font-mono">{fmt(stockAA?.rb)}</td>
                  <td className="text-right font-mono">{fmt(stockAA?.arb)}</td>
                  <td className="text-xs text-slate-700">
                    {Array.isArray(stockAA?.others) && stockAA.others.length > 0
                      ? stockAA.others.map((item) => `${item?.name || "-"}: ${item?.value || "-"}`).join(", ")
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Informasi Lainnya matrix */}
      <Card className="rounded-sm p-4 bg-white border-slate-200 mb-6" data-testid="widget-infos">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="label-ind">Informasi Lainnya</div>
            <h3 className="text-base font-semibold text-slate-900">Catatan per Shift</h3>
          </div>
          <span className="text-xs text-slate-500 font-mono">{infos.length} tanggal</span>
        </div>
        <div className="overflow-x-auto border border-slate-200 rounded-sm">
          <table className="w-full table-dense align-top">
            <thead>
              <tr>
                <th className="text-left w-12">No</th>
                <th className="text-left w-32">Tanggal</th>
                <th className="text-left">Shift 1</th>
                <th className="text-left">Shift 2</th>
                <th className="text-left">Shift 3</th>
              </tr>
            </thead>
            <tbody>
              {infos.length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-slate-400">Tidak ada informasi di rentang ini.</td></tr>
              )}
              {infos.map((row, i) => (
                <tr key={row.tanggal} data-testid={`info-row-${i}`} className="align-top">
                  <td className="font-mono text-slate-400 pt-2">{i + 1}</td>
                  <td className="font-mono pt-2">{row.tanggal}</td>
                  <ShiftInfoCell items={row.shift_1} testid="info-s1" />
                  <ShiftInfoCell items={row.shift_2} testid="info-s2" />
                  <ShiftInfoCell items={row.shift_3} testid="info-s3" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Info Meeting */}
      <Card className="rounded-sm p-4 bg-white border-slate-200 mb-6" data-testid="widget-meeting">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="label-ind">Info Meeting</div>
            <h3 className="text-base font-semibold text-slate-900">Briefing & Catatan Meeting</h3>
          </div>
          <span className="text-xs text-slate-500 font-mono">{meetings.length} entri</span>
        </div>
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          {meetings.length === 0 && (
            <div className="p-6 text-center text-slate-400 text-sm">Tidak ada info meeting di rentang ini.</div>
          )}
          {meetings.map((m, i) => (
            <div key={m.id} className={`p-4 ${i > 0 ? "border-t border-slate-200" : ""}`} data-testid={`meeting-row-${i}`}>
              <div className="flex items-center gap-2 text-xs mb-1.5">
                <span className="font-mono text-slate-700">{m.tanggal}</span>
                <span className="px-1.5 py-0.5 bg-slate-100 text-slate-700 font-semibold rounded-sm">Shift {m.shift}</span>
                <span className="text-[10px] font-mono text-slate-400 ml-auto">{m.id}</span>
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{m.isi_informasi}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function KumulatifColumn({ title, rows, accent, bg, testid }) {
  return (
    <div className="border border-slate-200 rounded-sm bg-slate-50 overflow-hidden" data-testid={testid}>
      <div className={`px-4 py-2 border-b border-slate-200 bg-white ${accent}`}>
        <div className="text-sm font-bold uppercase tracking-[0.12em]">{title}</div>
      </div>
      <div className="divide-y divide-slate-200">
        {rows.length === 0 && (
          <div className="p-6 text-center text-slate-400 text-sm">Tidak ada data di rentang ini.</div>
        )}
        {rows.map((r, i) => {
          const pct = Math.min(Math.max(r.percent, 0), 150);
          return (
            <div key={`${r.tanggal}-${r.shift}-${i}`} className="p-4 space-y-2" data-testid={`${testid}-row-${i}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[11px] tracking-[0.12em] uppercase font-semibold text-slate-500">
                    {r.tanggal} · Shift {r.shift}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">Team {r.team_operasi || "-"} · IF {r.team_if || "-"}</div>
                </div>
                <div className={`text-xs font-mono px-2 py-0.5 rounded-sm ${r.percent >= 100 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {(r.percent ?? 0).toFixed(1)}%
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <Cell label="Schedule" val={r.schedule} />
                <Cell label={`Total ${title}`} val={r.total} />
                <Cell label="Selisih Shift" val={r.selisih} signed />
              </div>
              <div>
                <div className="h-1.5 bg-slate-200 rounded-sm overflow-hidden">
                  <div className={`h-full ${bg} transition-all`} style={{ width: `${(pct / 150) * 100}%` }} />
                </div>
              </div>
              <div className="flex items-center justify-between pt-1 text-xs">
                <div className="text-slate-500">Akumulasi s/d sini</div>
                <div className={`font-mono font-bold ${r.akumulasi >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid={`${testid}-akum-${i}`}>
                  {r.akumulasi >= 0 ? "+" : ""}{Number(r.akumulasi || 0).toLocaleString("id-ID")}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Cell({ label, val, signed }) {
  const num = Number(val || 0);
  const color = signed ? (num >= 0 ? "text-emerald-700" : "text-red-600") : "text-slate-900";
  return (
    <div>
      <div className="label-ind">{label}</div>
      <div className={`font-mono font-bold text-base ${color}`}>
        {signed && num >= 0 ? "+" : ""}{num.toLocaleString("id-ID")}
      </div>
    </div>
  );
}

function ShiftInfoCell({ items, testid }) {
  if (!items || items.length === 0) {
    return <td className="text-slate-300 pt-2">—</td>;
  }
  return (
    <td className="pt-2 pb-2" data-testid={testid}>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-xs">
            <span className="font-semibold text-slate-800">{it.judul}</span>
            {it.isian && <span className="text-slate-600">: {it.isian}</span>}
          </li>
        ))}
      </ul>
    </td>
  );
}

function ProblemWidget({ problems }) {
  // group by (tanggal, shift)
  const groups = [];
  const map = {};
  for (const p of problems) {
    const k = `${p.tanggal}__${p.shift}`;
    if (!map[k]) {
      map[k] = { tanggal: p.tanggal, shift: p.shift, items: [] };
      groups.push(map[k]);
    }
    map[k].items.push(p);
  }
  return (
    <Card className="rounded-sm p-4 bg-white border-slate-200 lg:col-span-2" data-testid="widget-problem">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="label-ind">Problem</div>
          <h3 className="text-base font-semibold text-slate-900">Problem & Countermeasure</h3>
        </div>
        <span className="text-xs text-slate-500 font-mono">{problems.length} catatan</span>
      </div>
      <div className="border border-slate-200 rounded-sm overflow-hidden">
        {groups.length === 0 && (
          <div className="p-6 text-center text-slate-400 text-sm">Tidak ada problem di rentang ini.</div>
        )}
        {groups.map((g, gi) => (
          <div key={`${g.tanggal}-${g.shift}`} className={gi > 0 ? "border-t-2 border-slate-200" : ""} data-testid={`problem-group-${gi}`}>
            <div className="bg-slate-100 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-700 border-b border-slate-200">
              {g.tanggal} · Shift {g.shift} <span className="text-slate-400 font-normal normal-case">({g.items.length})</span>
            </div>
            <table className="w-full table-dense">
              <thead>
                <tr>
                  <th className="text-left w-20">Mulai</th>
                  <th className="text-left w-20">Selesai</th>
                  <th className="text-left w-36">Equipment</th>
                  <th className="text-left">Problem</th>
                  <th className="text-left">Countermeasure</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((row, i) => (
                  <tr key={i}>
                    <td className="font-mono">{row.waktu_mulai || "-"}</td>
                    <td className="font-mono">{row.waktu_selesai || "-"}</td>
                    <td className="font-semibold text-slate-800">{row.equipment || "-"}</td>
                    <td className="text-slate-700">{row.problem || "-"}</td>
                    <td className="text-slate-700">{row.countermeasure || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </Card>
  );
}
